import type { BlogPost } from './blogTypes';

// Validation error types
export interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

// Validation result
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

// Constants for validation
const MIN_TITLE_LENGTH = 10;
const MAX_TITLE_LENGTH = 100;
const MIN_EXCERPT_LENGTH = 50;
const MAX_EXCERPT_LENGTH = 500; // Increased from 300 to be more practical
const MIN_CONTENT_LENGTH = 300;
const VALID_CATEGORIES = [
  // Original categories from TinaCMS
  'Market Insights', 
  'Retirement Planning', 
  'Tax Strategies', 
  'Investment Tips', 
  'Financial Education',
  // Existing categories from current blog posts
  'Business Strategy',
  'Family Planning',
  'Farm Succession',
  'Personal Finance',
  // Allow uncategorized posts
  'Uncategorized',
  'General'
];
// More nuanced compliance checking - these phrases are problematic
const FORBIDDEN_PHRASES = [
  'guaranteed returns',
  'risk-free investment',
  'no-loss strategy',
  'always profitable',
  'guaranteed profit'
];
// Note: "guarantee" alone is OK in context like "CDIC guarantee" or "manufacturer's guarantee"
const IMAGE_FORMATS = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
const MAX_TAGS = 7; // Increased from 5 to allow more tags

/**
 * Validates a blog post before publishing
 */
export function validateBlogPost(post: Partial<BlogPost>): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Title validation
  if (!post.title || post.title.trim().length === 0) {
    errors.push({
      field: 'title',
      message: 'Title is required',
      severity: 'error'
    });
  } else if (post.title.length < MIN_TITLE_LENGTH) {
    errors.push({
      field: 'title',
      message: `Title must be at least ${MIN_TITLE_LENGTH} characters`,
      severity: 'error'
    });
  } else if (post.title.length > MAX_TITLE_LENGTH) {
    errors.push({
      field: 'title',
      message: `Title must be less than ${MAX_TITLE_LENGTH} characters`,
      severity: 'error'
    });
  }

  // Excerpt validation
  if (!post.excerpt || post.excerpt.trim().length === 0) {
    errors.push({
      field: 'excerpt',
      message: 'Excerpt is required for SEO and preview',
      severity: 'error'
    });
  } else if (post.excerpt.length < MIN_EXCERPT_LENGTH) {
    warnings.push({
      field: 'excerpt',
      message: `Excerpt should be at least ${MIN_EXCERPT_LENGTH} characters for better SEO`,
      severity: 'warning'
    });
  } else if (post.excerpt.length > MAX_EXCERPT_LENGTH) {
    errors.push({
      field: 'excerpt',
      message: `Excerpt must be less than ${MAX_EXCERPT_LENGTH} characters`,
      severity: 'error'
    });
  }

  // Category validation
  if (!post.category) {
    errors.push({
      field: 'category',
      message: 'Category is required',
      severity: 'error'
    });
  } else if (!VALID_CATEGORIES.includes(post.category)) {
    errors.push({
      field: 'category',
      message: `Category must be one of: ${VALID_CATEGORIES.join(', ')}`,
      severity: 'error'
    });
  }

  // Tags validation
  if (!post.tags || post.tags.length === 0) {
    warnings.push({
      field: 'tags',
      message: 'Consider adding tags for better discoverability',
      severity: 'warning'
    });
  } else if (post.tags.length > MAX_TAGS) {
    errors.push({
      field: 'tags',
      message: `Maximum ${MAX_TAGS} tags allowed`,
      severity: 'error'
    });
  }

  // Content validation
  if (!post.content || post.content.trim().length === 0) {
    errors.push({
      field: 'content',
      message: 'Content is required',
      severity: 'error'
    });
  } else if (post.content.length < MIN_CONTENT_LENGTH) {
    errors.push({
      field: 'content',
      message: `Content must be at least ${MIN_CONTENT_LENGTH} characters`,
      severity: 'error'
    });
  }

  // Check for forbidden phrases in content
  if (post.content) {
    const lowerContent = post.content.toLowerCase();
    const foundForbidden = FORBIDDEN_PHRASES.filter(phrase => lowerContent.includes(phrase));
    if (foundForbidden.length > 0) {
      errors.push({
        field: 'content',
        message: `Content contains compliance-violating phrases: ${foundForbidden.join(', ')}`,
        severity: 'error'
      });
    }
  }

  // Slug validation
  if (!post.slug || !/^[a-z0-9-]+$/.test(post.slug)) {
    errors.push({
      field: 'slug',
      message: 'Slug must contain only lowercase letters, numbers, and hyphens',
      severity: 'error'
    });
  }

  // Featured image validation
  if (post.featuredImage) {
    const hasValidExtension = IMAGE_FORMATS.some(ext => 
      post.featuredImage!.toLowerCase().endsWith(ext)
    );
    if (!hasValidExtension) {
      errors.push({
        field: 'featuredImage',
        message: `Image must be one of: ${IMAGE_FORMATS.join(', ')}`,
        severity: 'error'
      });
    }
  } else {
    warnings.push({
      field: 'featuredImage',
      message: 'Consider adding a featured image for better engagement',
      severity: 'warning'
    });
  }

  // Date validation
  if (!post.publishDate) {
    warnings.push({
      field: 'publishDate',
      message: 'No publish date set - will default to today',
      severity: 'warning'
    });
  }

  // Compliance validation
  if (!post.compliance) {
    warnings.push({
      field: 'compliance',
      message: 'Consider adding compliance disclosure',
      severity: 'warning'
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Sanitizes blog post data before saving
 */
export function sanitizeBlogPost(post: Partial<BlogPost>): Partial<BlogPost> {
  return {
    ...post,
    title: post.title?.trim(),
    excerpt: post.excerpt?.trim(),
    content: post.content?.trim(),
    slug: post.slug?.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-'),
    tags: post.tags?.map(tag => tag.trim().toLowerCase()),
    publishDate: post.publishDate || new Date().toISOString()
  };
}

/**
 * Generates a slug from title
 */
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .substring(0, 50);
}

/**
 * Estimates reading time based on content
 */
export function calculateReadTime(content: string): number {
  const wordsPerMinute = 200;
  const wordCount = content.split(/\s+/).length;
  return Math.ceil(wordCount / wordsPerMinute);
}

/**
 * Checks if a slug already exists
 */
export async function isSlugUnique(slug: string, existingPosts: BlogPost[]): Promise<boolean> {
  return !existingPosts.some(post => post.slug === slug);
}

/**
 * Validates image URL or path
 */
export function validateImageUrl(url: string): { isValid: boolean; error?: string } {
  if (!url) {
    return { isValid: false, error: 'Image URL is required' };
  }

  // Check if it's a relative path or absolute URL
  if (url.startsWith('/') || url.startsWith('http://') || url.startsWith('https://')) {
    const hasValidExtension = IMAGE_FORMATS.some(ext => url.toLowerCase().includes(ext));
    if (!hasValidExtension) {
      return { 
        isValid: false, 
        error: `Image must be one of: ${IMAGE_FORMATS.join(', ')}` 
      };
    }
    return { isValid: true };
  }

  return { isValid: false, error: 'Image must be a valid URL or path' };
}